import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useWebSocketContext } from '../providers/WebSocketProvider';
import {
  useSendWorkspaceMessageMutation,
  useStopWorkspaceSessionMutation,
} from '../store/api/workspaceApi';
import type { ExchangeEvent } from './useWebSocket';
import type {
  WorkspacePlan,
  WorkspaceArtefact,
  WorkspacePlanStep,
} from '../store/api/workspaceApi';

/** Map backend job.status strings to plan step status (strict union). */
function mapJobEventStatusToPlanStepStatus(
  jobStatus: string,
): WorkspacePlanStep['status'] | null {
  if (jobStatus === 'running' || jobStatus === 'started') return 'running';
  if (jobStatus === 'completed') return 'completed';
  if (jobStatus === 'failed') return 'failed';
  if (jobStatus === 'pending') return 'pending';
  return null;
}

/** Map backend artefact category to a valid WorkspaceTab. */
function categoryToTab(category: string | undefined): WorkspaceTab {
  switch (category) {
    case 'plans':
    case 'explore':
      return 'explore';
    case 'technical':
    case 'analyze':
      return 'analyze';
    case 'build':
      return 'build';
    default:
      return 'explore';
  }
}

export type WorkspaceTab = 'explore' | 'analyze' | 'build';

export type InteractionMode = 'auto' | 'ask' | 'explore' | 'build';

function newWorkspaceSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Local plan status union. ``approved`` is intentionally omitted in the
 * simplified WMA flow — there is no plan-approval gate; the WMA goes
 * directly from ``draft`` to ``executing`` once it dispatches build jobs.
 * The backend column still accepts ``approved`` for forward compatibility,
 * but the portal no longer surfaces or sets it.
 */
export type PlanStatus =
  | 'draft'
  | 'executing'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
}

export interface ContextSize {
  sessionTotalTokens: number;
  modelContextLimit: number;
  model: string;
}

export type ActivityDotColor = 'teal' | 'blue' | 'orange' | 'red' | 'green';

export type ActivityStatus = 'active' | 'completed' | 'error';

export interface ProcessingActivity {
  id: string;
  label: string;
  detail?: string;
  dotColor: ActivityDotColor;
  status: ActivityStatus;
  timestamp: number;
}

export type InputRequestInputType =
  | 'single_choice'
  | 'multi_choice'
  | 'free_text';

export interface InputRequestOption {
  id: string;
  label: string;
  description?: string;
}

export interface InputRequestSchema {
  inputType: InputRequestInputType | string;
  options?: InputRequestOption[];
  allowFreeText?: boolean;
  required?: boolean;
  minSelections?: number;
}

export type InputRequestStatus = 'awaiting' | 'submitting' | 'answered';

export interface TurnInputRequest {
  requestId: string;
  prompt: string;
  inputSchema: InputRequestSchema;
  requestingAgent?: string;
  status: InputRequestStatus;
  answer?: unknown;
  humanReadableAnswer?: string;
}

export interface TurnUserPrompt {
  content: string;
  timestamp: string;
  /** True when the turn was inserted as an auto-answer to an input request. */
  isSynthetic?: boolean;
  /** True when the "auto" badge should render next to the author. */
  autoBadge?: boolean;
  /** The request_id this synthetic turn answered, if any. */
  requestId?: string;
  /**
   * True when this turn is a placeholder for **background / follow-up** work
   * (e.g. WVA sub-agent dispatched after the WMA reply has been rendered).
   * The turn has no user prompt — the UI should render the activity strip
   * directly as a new "processing" block beneath the prior response.
   */
  isFollowUp?: boolean;
}

export type TurnStatus =
  | 'running'
  | 'awaiting_input'
  | 'completed'
  | 'failed';

/**
 * Reasoning preview the WMA emits at the start of non-trivial Explore/Build
 * turns via the ``exchange.thinking`` event. Surfaced as a soft pill above
 * the activity strip; cleared automatically when the next concrete activity
 * (job, sub-agent, step) arrives or the exchange completes.
 */
export interface TurnThinking {
  summary: string;
  at: number;
}

export interface Turn {
  id: string;
  index: number;
  userPrompt: TurnUserPrompt;
  activities: ProcessingActivity[];
  assistantContent: string;
  inputRequest?: TurnInputRequest;
  thinking?: TurnThinking;
  status: TurnStatus;
  errorSummary?: string;
  startTime: number;
  durationMs?: number;
  createdAt: string;
}

export interface PendingInputRequest {
  turnId: string;
  requestId: string;
  prompt: string;
  inputSchema: InputRequestSchema;
  requestingAgent?: string;
}

export interface SystemNotification {
  id: string;
  kind: 'info' | 'warning' | 'error';
  content: string;
  timestamp: string;
}

export interface SubmitInputResponseArgs {
  answer: unknown;
  humanReadableAnswer: string;
}

export interface UseWorkspaceReturn {
  activeTab: WorkspaceTab;
  setActiveTab: (tab: WorkspaceTab) => void;
  interactionMode: InteractionMode;
  setInteractionMode: (mode: InteractionMode) => void;
  resolvedMode: 'ask' | 'explore' | 'build' | null;
  effectiveMode: 'ask' | 'explore' | 'build' | null;
  currentPlan: WorkspacePlan | null;
  currentArtefact: WorkspaceArtefact | null;
  sessionId: string | null;
  contextSize: ContextSize | null;
  turns: Turn[];
  systemNotifications: SystemNotification[];
  pendingInputRequest: PendingInputRequest | null;
  /** Derived chat-message view. Preserved for legacy consumers. */
  messages: ChatMessage[];
  isProcessing: boolean;
  /**
   * True when at least one turn is still running or awaiting input.
   * Use this (rather than ``isProcessing`` alone) to drive the stop button
   * so it does not stick "on" after the final response has been delivered.
   */
  hasActiveTurn: boolean;
  isSending: boolean;
  canvasHtml: Record<WorkspaceTab, string | null>;
  canvasUrl: Record<WorkspaceTab, string | null>;
  processingActivities: ProcessingActivity[];
  processingStartTime: number | null;
  sendMessage: (query: string, sessionId?: string, projectId?: string) => void;
  submitInputResponse: (args: SubmitInputResponseArgs) => void;
  stopSession: () => void;
  resetSession: () => void;
}

function parseInputSchema(raw: unknown): InputRequestSchema | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const inputType = (r.input_type ?? r.inputType) as string | undefined;
  if (!inputType) return null;

  const optionsRaw = (r.options ?? []) as Array<Record<string, unknown>>;
  const options: InputRequestOption[] = Array.isArray(optionsRaw)
    ? optionsRaw
        .filter((o) => o && typeof o === 'object')
        .map((o) => ({
          id: String(o.id ?? ''),
          label: String(o.label ?? o.id ?? ''),
          description: (o.description as string | undefined) ?? undefined,
        }))
        .filter((o) => o.id && o.label)
    : [];

  return {
    inputType,
    options: options.length ? options : undefined,
    allowFreeText: Boolean(r.allow_free_text ?? r.allowFreeText),
    required: r.required !== false,
    minSelections: (r.min_selections ?? r.minSelections) as number | undefined,
  };
}

export function useWorkspace(): UseWorkspaceReturn {
  const [activeTab, setActiveTab] = useState<WorkspaceTab>('explore');
  const [interactionMode, setInteractionModeRaw] = useState<InteractionMode>('auto');
  const [resolvedMode, setResolvedMode] = useState<'ask' | 'explore' | 'build' | null>(null);
  const [currentPlan, setCurrentPlan] = useState<WorkspacePlan | null>(null);
  const [currentArtefact, setCurrentArtefact] = useState<WorkspaceArtefact | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(() => newWorkspaceSessionId());
  const [contextSize, setContextSize] = useState<ContextSize | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [systemNotifications, setSystemNotifications] = useState<
    SystemNotification[]
  >([]);
  const [pendingInputRequest, setPendingInputRequest] =
    useState<PendingInputRequest | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingActivities, setProcessingActivitiesState] = useState<
    ProcessingActivity[]
  >([]);
  const [processingStartTime, setProcessingStartTime] = useState<number | null>(
    null,
  );
  const [canvasHtml, setCanvasHtml] = useState<Record<WorkspaceTab, string | null>>({
    explore: null,
    analyze: null,
    build: null,
  });
  const [canvasUrl, setCanvasUrl] = useState<Record<WorkspaceTab, string | null>>({
    explore: null,
    analyze: null,
    build: null,
  });

  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  const setInteractionMode = useCallback((mode: InteractionMode) => {
    setInteractionModeRaw(mode);
    setResolvedMode(null);
  }, []);

  const effectiveMode: 'ask' | 'explore' | 'build' | null =
    interactionMode !== 'auto' ? interactionMode : resolvedMode;

  const { addListener, removeListener, subscribe, unsubscribe } =
    useWebSocketContext();

  const [sendWorkspaceMessage, { isLoading: isSending }] =
    useSendWorkspaceMessageMutation();
  const [stopWorkspaceSession] = useStopWorkspaceSessionMutation();

  /**
   * Update the most recent turn in place. Used by every WebSocket handler
   * that mutates the active turn's activities, status, assistant content,
   * duration, or input request. Returns unchanged when there are no turns.
   */
  const mutateLastTurn = useCallback(
    (mutator: (turn: Turn) => Turn) => {
      setTurns((prev) => {
        if (prev.length === 0) return prev;
        const next = [...prev];
        const last = next[next.length - 1];
        next[next.length - 1] = mutator(last);
        return next;
      });
    },
    [],
  );

  const mutateLastRunningTurn = useCallback(
    (mutator: (turn: Turn) => Turn) => {
      setTurns((prev) => {
        if (prev.length === 0) return prev;
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].status === 'running' || next[i].status === 'awaiting_input') {
            next[i] = mutator(next[i]);
            return next;
          }
        }
        return prev;
      });
    },
    [],
  );

  /**
   * Ensure there is an *active* (running / awaiting_input) turn to attach
   * fresh activity events to. If the last turn is already completed/failed
   * **and** has an assistant response, spawn a synthetic "follow-up" turn
   * so the new processing block renders **beneath** the completed response
   * instead of re-opening the previous turn's activity strip.
   *
   * Returns the (possibly new) turns array after the update was scheduled.
   */
  const ensureActiveTurn = useCallback(() => {
    setTurns((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (last.status === 'running' || last.status === 'awaiting_input') {
        return prev;
      }
      // Only spawn a follow-up turn if the last turn actually produced a
      // user-visible response. If it failed outright we want the new event
      // to stay quiet rather than grow a second orphan block.
      if (!last.assistantContent) return prev;
      const now = Date.now();
      return [
        ...prev,
        {
          id: `turn-followup-${now}`,
          index: prev.length,
          userPrompt: {
            content: '',
            timestamp: new Date(now).toISOString(),
            isFollowUp: true,
          },
          activities: [],
          assistantContent: '',
          status: 'running',
          startTime: now,
          createdAt: new Date(now).toISOString(),
        },
      ];
    });
  }, []);

  /**
   * Update the activity stream. Mirrored across two places:
   *
   * 1. The flat ``processingActivities`` state, which existing consumers
   *    (tests and legacy components) read directly.
   * 2. The ``activities`` array on the most recent turn, so the turn
   *    document can render the strip inline with the turn.
   *
   * Keeping both in sync avoids a big-bang rewrite of every listener.
   */
  const updateActivities = useCallback(
    (updater: (prev: ProcessingActivity[]) => ProcessingActivity[]) => {
      // Make sure activity lands on a *live* turn. If the previous turn has
      // already rendered its assistant response, spawn a follow-up turn so
      // the processing block physically moves beneath the prior reply.
      ensureActiveTurn();
      setProcessingActivitiesState((prev) => {
        const next = updater(prev);
        // Concrete activity supersedes the transient "thinking…" preview:
        // the WMA has moved on to dispatching work, so clear the pill in
        // the same render to avoid visual stutter.
        mutateLastTurn((t) =>
          t.thinking
            ? { ...t, activities: next, thinking: undefined }
            : { ...t, activities: next },
        );
        return next;
      });
    },
    [ensureActiveTurn, mutateLastTurn],
  );

  const addSystemNotification = useCallback(
    (kind: SystemNotification['kind'], content: string) => {
      setSystemNotifications((prev) => [
        ...prev,
        {
          id: `sys-${Date.now()}-${prev.length}`,
          kind,
          content,
          timestamp: new Date().toISOString(),
        },
      ]);
    },
    [],
  );

  const handleWsEvent = useCallback(
    (event: ExchangeEvent) => {
      if (event.sessionId && sessionIdRef.current && event.sessionId !== sessionIdRef.current) {
        return;
      }

      switch (event.type) {
        case 'exchange.completed': {
          const usage = event.data?.llm_usage;
          if (usage) {
            setContextSize({
              sessionTotalTokens: usage.session_total_tokens ?? 0,
              modelContextLimit: usage.model_context_limit ?? 128000,
              model: usage.model ?? '',
            });
          }

          const html = event.data?.html;
          const targetTab = event.data?.target_tab as WorkspaceTab | undefined;
          if (html && targetTab) {
            setCanvasHtml((prev) => ({ ...prev, [targetTab]: html }));
            setActiveTab(targetTab);
          }

          if (event.data?.plan) {
            setCurrentPlan(event.data.plan as WorkspacePlan);
          }
          if (event.data?.artefact) {
            setCurrentArtefact(event.data.artefact as WorkspaceArtefact);
          }
          // ``exchange.completed`` is the authoritative "pipeline finished"
          // signal from the harness. Any active activity bullets that never
          // got their matching ``step.completed`` event (e.g. a background
          // job that crashed silently) get rolled up so the strip can
          // collapse into its "Worked for Ns" pill.
          updateActivities((prev) =>
            prev.map((a) =>
              a.status === 'active'
                ? { ...a, status: 'completed' as const, dotColor: 'green' as const }
                : a,
            ),
          );
          // Kill the spinner here too: on large payloads the follow-up
          // ``conversation.result`` arrives as a fetch-required stub and the
          // frontend never sees the text response, leaving the stop button
          // stuck "on". Completing the exchange is enough signal.
          setIsProcessing(false);
          setProcessingStartTime(null);
          // Close out the trailing follow-up turn (if any) so its strip
          // collapses into a "Worked for Ns" pill. Main turns are closed
          // by ``conversation.result`` once the final response arrives.
          mutateLastRunningTurn((t) =>
            t.userPrompt.isFollowUp
              ? {
                  ...t,
                  status: 'completed',
                  durationMs: Date.now() - t.startTime,
                  thinking: undefined,
                }
              : t.thinking
              ? { ...t, thinking: undefined }
              : t,
          );
          break;
        }

        case 'conversation.result': {
          setIsProcessing(false);
          setProcessingStartTime(null);
          const responseText = event.data?.response as string | undefined;
          const fetchRequired = Boolean(event.data?.fetch_required);

          setTurns((prev) => {
            if (prev.length === 0) {
              // Assistant-initiated message (no prior user prompt in this
              // session). Create an empty-prompt turn so the legacy
              // ``messages`` view and the new turn list both see the reply.
              if (!responseText) return prev;
              const now = Date.now();
              return [
                {
                  id: `turn-${now}`,
                  index: 0,
                  userPrompt: { content: '', timestamp: new Date(now).toISOString() },
                  activities: [],
                  assistantContent: responseText,
                  status: 'completed',
                  startTime: now,
                  durationMs: 0,
                  createdAt: new Date(now).toISOString(),
                },
              ];
            }
            const next = [...prev];
            const lastIdx = next.length - 1;
            const last = next[lastIdx];
            next[lastIdx] = {
              ...last,
              assistantContent: responseText || last.assistantContent,
              status:
                last.inputRequest && last.inputRequest.status !== 'answered'
                  ? 'awaiting_input'
                  : 'completed',
              durationMs: Date.now() - last.startTime,
              thinking: undefined,
            };
            return next;
          });
          if (fetchRequired && !responseText) {
            // The full payload was elided because it exceeded the WS
            // threshold. Surface a notification so the user knows to reload
            // the session to retrieve the stored response.
            addSystemNotification(
              'info',
              'Response was too large to stream. Reload the session to view the full reply.',
            );
          }
          const resultActions = event.data?.actions as Array<Record<string, unknown>> | undefined;
          if (resultActions && interactionMode === 'auto') {
            const modeAction = resultActions.find((a) => a.action === 'mode_resolved');
            if (modeAction?.resolved_mode) {
              setResolvedMode(modeAction.resolved_mode as 'ask' | 'explore' | 'build');
            }
          }
          break;
        }

        case 'exchange.response.completed':
          break;

        case 'exchange.started':
          setIsProcessing(true);
          setProcessingStartTime(Date.now());
          setProcessingActivitiesState([]);
          mutateLastTurn((t) =>
            t.status === 'running' ? { ...t, activities: [] } : t,
          );
          break;

        case 'exchange.thinking': {
          const summary = (event.data?.summary ||
            event.data?.content ||
            event.data?.text ||
            '') as string;
          if (!summary.trim()) break;
          // Make sure the thinking pill lands on a *live* turn so the
          // activity strip and the pill render together. Mirrors the
          // ensureActiveTurn() guard that ``updateActivities`` uses.
          ensureActiveTurn();
          mutateLastRunningTurn((t) => ({
            ...t,
            thinking: { summary: summary.trim(), at: Date.now() },
          }));
          break;
        }

        case 'exchange.step.started': {
          const rawStep = event.data?.step || event.data?.step_name || event.data?.name || 'Step';
          const agentLabel = event.data?.agent || event.data?.agent_name;
          let stepLabel = rawStep as string;
          let dotColor: ActivityDotColor = 'blue';

          if (rawStep === 'fan_out') {
            const agents = event.data?.agents;
            const count = Array.isArray(agents) ? agents.length : '';
            const agentNames = Array.isArray(agents) ? (agents as string[]).join(', ') : '';
            stepLabel = count ? `Delegating to ${count} agents` : 'Fan-out';
            updateActivities((prev) => [
              ...prev,
              {
                id: `step-fanout-${Date.now()}`,
                label: stepLabel,
                detail: agentNames || undefined,
                dotColor: 'blue',
                status: 'active',
                timestamp: Date.now(),
              },
            ]);
            break;
          }

          if (rawStep === 'fan_out_agent' && agentLabel) {
            stepLabel = `Agent: ${agentLabel}`;
          } else if (rawStep === 'llm') {
            stepLabel = 'Reasoning with LLM...';
            dotColor = 'teal';
          } else if (agentLabel) {
            stepLabel = `Engaging ${agentLabel}`;
          }

          const stepId = event.data?.step_id || event.data?.stepId || rawStep;
          const activityId =
            rawStep === 'fan_out_agent' && agentLabel
              ? `step-fanout-${agentLabel}`
              : `step-${stepId}-${Date.now()}`;

          updateActivities((prev) => [
            ...prev,
            {
              id: activityId,
              label: stepLabel,
              detail: (event.data?.message || event.data?.description || '') as string || undefined,
              dotColor,
              status: 'active',
              timestamp: Date.now(),
            },
          ]);
          break;
        }

        case 'exchange.step.completed': {
          const rawStepC = event.data?.step || event.data?.step_name;
          const agentLabelC = event.data?.agent || event.data?.agent_name;
          const stepKeyC =
            rawStepC === 'fan_out_agent' && agentLabelC
              ? `step-fanout-${agentLabelC}`
              : null;

          updateActivities((prev) => {
            const updated = [...prev];
            for (let i = updated.length - 1; i >= 0; i--) {
              if (stepKeyC && updated[i].id === stepKeyC && updated[i].status === 'active') {
                updated[i] = { ...updated[i], status: 'completed', dotColor: 'green' };
                break;
              }
              if (!stepKeyC && updated[i].status === 'active') {
                updated[i] = { ...updated[i], status: 'completed', dotColor: 'green' };
                break;
              }
            }
            return updated;
          });
          break;
        }

        case 'exchange.step.failed': {
          const rawStepF = event.data?.step || event.data?.step_name;
          const agentLabelF = event.data?.agent || event.data?.agent_name;
          const stepKeyF =
            rawStepF === 'fan_out_agent' && agentLabelF
              ? `step-fanout-${agentLabelF}`
              : null;
          const errorMsg = (event.data?.error || event.data?.message || '') as string;

          updateActivities((prev) => {
            const updated = [...prev];
            for (let i = updated.length - 1; i >= 0; i--) {
              if (stepKeyF && updated[i].id === stepKeyF && updated[i].status === 'active') {
                updated[i] = { ...updated[i], status: 'error', dotColor: 'red', detail: errorMsg || updated[i].detail };
                break;
              }
              if (!stepKeyF && updated[i].status === 'active') {
                updated[i] = { ...updated[i], status: 'error', dotColor: 'red', detail: errorMsg || updated[i].detail };
                break;
              }
            }
            return updated;
          });
          break;
        }

        case 'exchange.subagent.started': {
          const target = (event.data?.target_agent || event.data?.targetAgent || 'sub-agent') as string;
          const activityId = `subagent-${target}`;
          updateActivities((prev) => [
            ...prev,
            {
              id: activityId,
              label: `Delegated to ${target}`,
              detail: undefined,
              dotColor: 'blue',
              status: 'active',
              timestamp: Date.now(),
            },
          ]);
          break;
        }

        case 'exchange.subagent.completed': {
          const targetC = (event.data?.target_agent || event.data?.targetAgent) as string | undefined;
          const activityKeyC = targetC ? `subagent-${targetC}` : null;
          updateActivities((prev) => {
            const updated = [...prev];
            for (let i = updated.length - 1; i >= 0; i--) {
              if (
                activityKeyC &&
                updated[i].id === activityKeyC &&
                updated[i].status === 'active'
              ) {
                updated[i] = { ...updated[i], status: 'completed', dotColor: 'green' };
                break;
              }
            }
            return updated;
          });
          break;
        }

        case 'exchange.subagent.failed': {
          const targetF = (event.data?.target_agent || event.data?.targetAgent) as string | undefined;
          const activityKeyF = targetF ? `subagent-${targetF}` : null;
          const errSub = (event.data?.error || event.data?.message || '') as string;
          updateActivities((prev) => {
            const updated = [...prev];
            for (let i = updated.length - 1; i >= 0; i--) {
              if (
                activityKeyF &&
                updated[i].id === activityKeyF &&
                updated[i].status === 'active'
              ) {
                updated[i] = {
                  ...updated[i],
                  status: 'error',
                  dotColor: 'red',
                  detail: errSub || updated[i].detail,
                };
                break;
              }
            }
            return updated;
          });
          break;
        }

        case 'exchange.failed': {
          setIsProcessing(false);
          setProcessingStartTime(null);
          const errText = (event.data?.error_summary ?? 'An error occurred.') as string;
          mutateLastTurn((t) => ({
            ...t,
            status: 'failed',
            errorSummary: errText,
            durationMs: Date.now() - t.startTime,
          }));
          addSystemNotification('error', errText);
          break;
        }

        case 'exchange.input_required': {
          // Informational placeholders (e.g. "Requesting integrations.task...")
          // are filtered by the crew-api webhook relay, but defend against
          // a stray one by ignoring anything without an input_schema.
          if (event.data?.informational) break;
          const schema = parseInputSchema(event.data?.input_schema);
          if (!schema) break;

          const requestId = (event.data?.request_id as string | undefined) ?? `req-${Date.now()}`;
          const prompt = (event.data?.prompt as string | undefined) ?? '';
          const requestingAgent =
            (event.data?.requesting_agent as string | undefined) ??
            (event.data?.agent_name as string | undefined) ??
            undefined;

          // Mutate the last running/awaiting turn and simultaneously derive the
          // turnId for ``pendingInputRequest``. Setting both states from within
          // the functional updater guarantees consistency even when React
          // batches/defers the updater call.
          setTurns((prev) => {
            if (prev.length === 0) return prev;
            let idx = -1;
            for (let i = prev.length - 1; i >= 0; i--) {
              const s = prev[i].status;
              if (s === 'running' || s === 'awaiting_input') {
                idx = i;
                break;
              }
            }
            if (idx < 0) return prev;
            const next = [...prev];
            const turn = next[idx];
            next[idx] = {
              ...turn,
              status: 'awaiting_input',
              inputRequest: {
                requestId,
                prompt,
                inputSchema: schema,
                requestingAgent,
                status: 'awaiting',
              },
            };
            setPendingInputRequest({
              turnId: turn.id,
              requestId,
              prompt,
              inputSchema: schema,
              requestingAgent,
            });
            return next;
          });
          break;
        }

        case 'job.status': {
          const jobStepId = event.data?.step_id;
          const jobStatus = event.data?.status as string | undefined;
          const jobType = (event.data?.job_type || event.data?.type || 'Job') as string;
          const jobMessage = (event.data?.message || '') as string;
          const jobActivityId = `job-${jobStepId || jobType}-${Date.now()}`;

          if (jobStatus === 'running' || jobStatus === 'started') {
            updateActivities((prev) => [
              ...prev,
              {
                id: jobActivityId,
                label: `Job: ${jobType}`,
                detail: jobMessage || undefined,
                dotColor: 'orange',
                status: 'active',
                timestamp: Date.now(),
              },
            ]);
          } else if (jobStatus === 'completed') {
            updateActivities((prev) => {
              const updated = [...prev];
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].label.startsWith('Job:') && updated[i].status === 'active') {
                  updated[i] = { ...updated[i], status: 'completed', dotColor: 'green', detail: jobMessage || updated[i].detail };
                  break;
                }
              }
              return updated;
            });
          } else if (jobStatus === 'failed') {
            updateActivities((prev) => {
              const updated = [...prev];
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].label.startsWith('Job:') && updated[i].status === 'active') {
                  updated[i] = { ...updated[i], status: 'error', dotColor: 'red', detail: jobMessage || updated[i].detail };
                  break;
                }
              }
              return updated;
            });
          }

          if (currentPlan && jobStepId && jobStatus) {
            const stepStatus = mapJobEventStatusToPlanStepStatus(jobStatus);
            if (stepStatus) {
              setCurrentPlan((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  steps: prev.steps.map((s) =>
                    s.stepId === jobStepId ? { ...s, status: stepStatus } : s,
                  ),
                };
              });
            }
          }
          break;
        }

        case 'exchange.mode_resolved': {
          const rm = event.data?.resolved_mode as 'ask' | 'explore' | 'build' | undefined;
          if (rm && interactionMode === 'auto') {
            setResolvedMode(rm);
          }
          break;
        }

        case 'context.summarized':
          setContextSize(null);
          addSystemNotification('info', 'Context summarised to continue the session.');
          break;

        case 'session.stopped':
          setIsProcessing(false);
          updateActivities((prev) =>
            prev.map((a) =>
              a.status === 'active'
                ? { ...a, status: 'completed' as const, dotColor: 'green' as const }
                : a,
            ),
          );
          mutateLastTurn((t) =>
            t.status === 'running' || t.status === 'awaiting_input'
              ? { ...t, status: 'completed', durationMs: Date.now() - t.startTime }
              : t,
          );
          addSystemNotification('info', 'Session stopped.');
          break;

        case 'artefact.ready': {
          const d = event.data ?? {};
          const presignedUrl = d.presigned_url || d.presignedUrl;
          const rawTab = d.target_tab || d.category;
          const tab = categoryToTab(rawTab);
          if (presignedUrl) {
            setCanvasUrl((prev) => ({ ...prev, [tab]: presignedUrl }));
            setActiveTab(tab);
          }
          if (d.artefact_id || d.artefactId) {
            setCurrentArtefact({
              id: d.artefact_id || d.artefactId,
              title: d.title || '',
              type: 'html',
              shareScope: 'private',
              createdAt: new Date().toISOString(),
            });
          }
          break;
        }

        case 'plan.created': {
          const plan = event.data?.plan as WorkspacePlan | undefined;
          if (plan) {
            setCurrentPlan(plan);
          }
          break;
        }

        case 'plan.updated': {
          const updStepId = event.data?.step_id || event.data?.stepId;
          const updStatus = event.data?.step_status || event.data?.stepStatus;
          if (updStepId && updStatus) {
            const stepStatus = mapJobEventStatusToPlanStepStatus(updStatus as string);
            if (stepStatus) {
              setCurrentPlan((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  steps: prev.steps.map((s) =>
                    s.stepId === updStepId ? { ...s, status: stepStatus } : s,
                  ),
                };
              });
            }
          }
          break;
        }
      }
    },
    [
      currentPlan,
      interactionMode,
      mutateLastTurn,
      mutateLastRunningTurn,
      updateActivities,
      addSystemNotification,
    ],
  );

  useEffect(() => {
    addListener(handleWsEvent);
    return () => removeListener(handleWsEvent);
  }, [addListener, removeListener, handleWsEvent]);

  useEffect(() => {
    if (sessionId) {
      subscribe(sessionId);
      return () => unsubscribe();
    }
  }, [sessionId, subscribe, unsubscribe]);

  /**
   * Safety net: if every turn reports ``completed`` / ``failed`` then the
   * pipeline is definitely done, regardless of whether the authoritative
   * completion event arrived. Clearing ``isProcessing`` here prevents the
   * stop button from sticking on (Issue #3 from user triage).
   */
  const hasActiveTurn = useMemo(
    () =>
      turns.some(
        (t) => t.status === 'running' || t.status === 'awaiting_input',
      ),
    [turns],
  );

  useEffect(() => {
    // Only fire the safety clear when at least one turn exists (otherwise
    // we would stomp on the legitimate "processing before first turn is
    // appended" window — e.g. tests and headless WS-only flows that emit
    // ``exchange.started`` without a prior user message).
    if (turns.length > 0 && !hasActiveTurn && isProcessing) {
      setIsProcessing(false);
      setProcessingStartTime(null);
    }
  }, [turns.length, hasActiveTurn, isProcessing]);

  const appendTurn = useCallback(
    (userPrompt: TurnUserPrompt): string => {
      const now = Date.now();
      const id = `turn-${now}`;
      setTurns((prev) => [
        ...prev,
        {
          id,
          index: prev.length,
          userPrompt,
          activities: [],
          assistantContent: '',
          status: 'running',
          startTime: now,
          createdAt: new Date(now).toISOString(),
        },
      ]);
      return id;
    },
    [],
  );

  const sendMessage = useCallback(
    async (query: string, overrideSessionId?: string, projectId?: string) => {
      const ts = new Date().toISOString();
      appendTurn({ content: query, timestamp: ts });
      setIsProcessing(true);

      try {
        const result = await sendWorkspaceMessage({
          query,
          sessionId: overrideSessionId ?? sessionId ?? undefined,
          projectId,
          interactionMode,
        }).unwrap();

        if (result.sessionId && result.sessionId !== sessionId) {
          setSessionId(result.sessionId);
        }

        const responseText = result.response;
        if (responseText) {
          mutateLastTurn((t) => ({
            ...t,
            assistantContent: responseText,
            status: t.inputRequest && t.inputRequest.status !== 'answered'
              ? 'awaiting_input'
              : 'completed',
            durationMs: Date.now() - t.startTime,
          }));
        }

        if (result.status !== 'processing') {
          setIsProcessing(false);
        }
      } catch {
        setIsProcessing(false);
        mutateLastTurn((t) => ({
          ...t,
          status: 'failed',
          errorSummary: 'Failed to send message.',
          durationMs: Date.now() - t.startTime,
        }));
        addSystemNotification('error', 'Failed to send message.');
      }
    },
    [
      appendTurn,
      sendWorkspaceMessage,
      sessionId,
      interactionMode,
      mutateLastTurn,
      addSystemNotification,
    ],
  );

  const submitInputResponse = useCallback(
    async ({ answer, humanReadableAnswer }: SubmitInputResponseArgs) => {
      if (!pendingInputRequest) return;
      const { turnId, requestId } = pendingInputRequest;

      // Mark the awaiting turn as answered (card collapses to summary).
      setTurns((prev) =>
        prev.map((t) =>
          t.id === turnId && t.inputRequest
            ? {
                ...t,
                status: 'completed',
                durationMs: t.durationMs ?? Date.now() - t.startTime,
                inputRequest: {
                  ...t.inputRequest,
                  status: 'answered',
                  answer,
                  humanReadableAnswer,
                },
              }
            : t,
        ),
      );
      setPendingInputRequest(null);

      // Append a synthetic user turn that records the answer so the
      // conversation document reads naturally.
      const ts = new Date().toISOString();
      appendTurn({
        content: humanReadableAnswer,
        timestamp: ts,
        isSynthetic: true,
        autoBadge: true,
        requestId,
      });
      setIsProcessing(true);

      try {
        const result = await sendWorkspaceMessage({
          query: humanReadableAnswer,
          sessionId: sessionId ?? undefined,
          interactionMode,
          inputResponse: { requestId, answer },
        }).unwrap();

        if (result.sessionId && result.sessionId !== sessionId) {
          setSessionId(result.sessionId);
        }

        const responseText = result.response;
        if (responseText) {
          mutateLastTurn((t) => ({
            ...t,
            assistantContent: responseText,
            status: 'completed',
            durationMs: Date.now() - t.startTime,
          }));
        }

        if (result.status !== 'processing') {
          setIsProcessing(false);
        }
      } catch {
        setIsProcessing(false);
        mutateLastTurn((t) => ({
          ...t,
          status: 'failed',
          errorSummary: 'Failed to submit response.',
          durationMs: Date.now() - t.startTime,
        }));
        addSystemNotification('error', 'Failed to submit response.');
      }
    },
    [
      pendingInputRequest,
      appendTurn,
      sendWorkspaceMessage,
      sessionId,
      interactionMode,
      mutateLastTurn,
      addSystemNotification,
    ],
  );

  const stopSession = useCallback(async () => {
    if (!sessionId) return;
    setIsProcessing(false);
    mutateLastTurn((t) =>
      t.status === 'running' || t.status === 'awaiting_input'
        ? {
            ...t,
            status: 'completed',
            durationMs: Date.now() - t.startTime,
            activities: t.activities.map((a) =>
              a.status === 'active'
                ? { ...a, status: 'completed' as const, dotColor: 'green' as const }
                : a,
            ),
          }
        : t,
    );
    setPendingInputRequest(null);
    addSystemNotification('info', 'Session stopped.');
    try {
      await stopWorkspaceSession(sessionId).unwrap();
    } catch {
      /* best effort -- UI already updated */
    }
  }, [sessionId, stopWorkspaceSession, mutateLastTurn, addSystemNotification]);

  const resetSession = useCallback(() => {
    setSessionId(newWorkspaceSessionId());
    setTurns([]);
    setSystemNotifications([]);
    setPendingInputRequest(null);
    setCurrentPlan(null);
    setCurrentArtefact(null);
    setContextSize(null);
    setIsProcessing(false);
    setProcessingActivitiesState([]);
    setProcessingStartTime(null);
    setCanvasHtml({ explore: null, analyze: null, build: null });
    setCanvasUrl({ explore: null, analyze: null, build: null });
    setInteractionModeRaw('auto');
    setResolvedMode(null);
  }, []);

  /* Derived views for legacy consumers. */

  /**
   * Derived legacy ``messages`` view. Errors are surfaced via
   * ``systemNotifications`` rather than per-turn ``errorSummary`` to avoid
   * double-counting when the turn UI and notification both fire.
   */
  const messages = useMemo<ChatMessage[]>(() => {
    const out: ChatMessage[] = [];
    for (const t of turns) {
      out.push({
        role: 'user',
        content: t.userPrompt.content,
        timestamp: t.userPrompt.timestamp,
      });
      if (t.assistantContent) {
        out.push({
          role: 'assistant',
          content: t.assistantContent,
          timestamp: t.createdAt,
        });
      }
    }
    for (const n of systemNotifications) {
      out.push({ role: 'system', content: n.content, timestamp: n.timestamp });
    }
    return out;
  }, [turns, systemNotifications]);

  return {
    activeTab,
    setActiveTab,
    interactionMode,
    setInteractionMode,
    resolvedMode,
    effectiveMode,
    currentPlan,
    currentArtefact,
    sessionId,
    contextSize,
    turns,
    systemNotifications,
    pendingInputRequest,
    messages,
    isProcessing,
    hasActiveTurn,
    isSending,
    canvasHtml,
    canvasUrl,
    processingActivities,
    processingStartTime,
    sendMessage,
    submitInputResponse,
    stopSession,
    resetSession,
  };
}
